import React from "react";
import SVG, { cacheStore } from "react-inlinesvg";
import styled from "styled-components";
import { type IconName } from "./iconNames.generated";

const iconModules = import.meta.glob("/src/assets/icons/*.svg", {
	eager: true,
	query: "?url",
	import: "default",
}) as Record<string, string>;

const toCamelCase = (value: string) =>
	value.replace(/[-_]([a-z0-9])/g, (_, character: string) => character.toUpperCase());

const IconMap = Object.fromEntries(
	Object.entries(iconModules).map(([path, url]) => {
		const fileName = path
			.split("/")
			.pop()!
			.replace(/\.svg$/, "");
		return [toCamelCase(fileName), url];
	}),
) as Record<IconName, string>;

const resolveLegacyIconSrc = (src: string) => {
	const match = src.match(/(?:^|\/)icons?\/([^/?#]+)\.svg(?:[?#].*)?$/);

	if (!match) {
		return src;
	}

	const iconName = toCamelCase(match[1]) as IconName;
	return IconMap[iconName] ?? src;
};

export type { IconName } from "./iconNames.generated";

/**
 * Warms react-inlinesvg's cache so these icons render on their first frame
 * instead of popping in after a fetch. Safe to call before React mounts.
 */
export const preloadIcons = (names: IconName[]) => {
	for (const name of names) {
		const url = IconMap[name];
		if (url) cacheStore.get(url).catch(() => {});
	}
};

interface CommonIconProps extends Omit<React.SVGProps<SVGSVGElement>, "name"> {
	size?: number | string;
	color?: string;
	alt?: string;
	preserveOriginalColor?: boolean;
}

export type IconProps = CommonIconProps &
	(
		| {
				name: IconName;
				src?: never;
		  }
		| {
				src: string;
				name?: never;
		  }
	);

const IconWrapper = styled.div<{ $size?: number | string; $color?: string; $preserveOriginalColor?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => (typeof props.$size === "number" ? `${props.$size}px` : props.$size || "24px")};
	height: ${(props) => (typeof props.$size === "number" ? `${props.$size}px` : props.$size || "24px")};

	svg {
		width: 100%;
		height: 100%;
		transition: color 0.2s ease;
		color: ${(props) => props.$color || "inherit"};

		path,
		circle,
		rect,
		line,
		polyline,
		polygon {
			${(props) =>
				props.$preserveOriginalColor ? "" : `stroke: ${props.$color ? props.$color : "currentColor"};`}
		}
	}
`;

/**
 * Renders an SVG from the auto-discovered icon set, or from a legacy `src`.
 * New call sites should use `name`; `src` remains supported during migration.
 */
export const Icon: React.FC<IconProps> = ({
	name,
	src,
	size,
	color,
	className,
	alt,
	preserveOriginalColor = false,
	...svgProps
}) => {
	const resolvedSrc = name ? IconMap[name] : src ? resolveLegacyIconSrc(src) : undefined;
	const InlineSVG = SVG as React.ComponentType<Record<string, unknown>>;

	if (!resolvedSrc) {
		console.warn(`Icon "${name}" not found. Available: ${Object.keys(IconMap).join(", ")}`);
		return null;
	}

	const hasAccessibleName = Boolean(alt || svgProps["aria-label"] || svgProps["aria-labelledby"]);

	return (
		<IconWrapper $size={size} $color={color} $preserveOriginalColor={preserveOriginalColor} className={className}>
			<InlineSVG
				src={resolvedSrc}
				title={alt || undefined}
				role={hasAccessibleName ? "img" : undefined}
				aria-hidden={hasAccessibleName ? undefined : true}
				{...svgProps}
			/>
		</IconWrapper>
	);
};
